import React, { ChangeEvent, useEffect, useState } from 'react';
import { Loading } from 'react-loading-dot';
import styled from 'styled-components';

import { useService } from '@xstate/react';

import { productBuilderMachine } from '../../../context/ProductBuilder/machine';
import { getBaseProduct } from '../../../helpers/getBaseProduct';
import { getBaseProductType } from '../../../helpers/getBaseProductType';
import { getCurrentVariant } from '../../../helpers/getCurrentVariant';
import { getProductType } from '../../../helpers/getProductType';
import { getRequiredDepthByProductTypeAndCellDesign } from '../../../helpers/getRequiredDepthByProductTypeAndCellDesign';
import { getRequiredDepthForRollers } from '../../../helpers/getRequiredDepthForRollers';
import { getRequiredDepthForRomans } from '../../../helpers/getRequiredDepthForRomans';
import { getRequiredDepthForShutters } from '../../../helpers/getRequiredDepthForShutters';
import { getRequiredDepthForVenetians } from '../../../helpers/getRequiredDepthForVenetians';
import { isCurrentState } from '../../../helpers/isCurrentState';
import { getAllowedValue } from '../../../helpers/measurements';
import {
  getMaxHeight,
  getMaxWidth,
  getMinHeight,
  getMinWidth,
  getRollerDayNightMaxWidth,
  getSmartglideMaxWidth,
} from '../../../helpers/metafields';
import { throwError } from '../../../helpers/throwError';
import { ProductBuilderMachineEvent } from '../../../types';
import { Caption } from '../../Common/Caption';
import { Heading } from '../../Common/Heading';
import { MeasurementInput } from '../../Common/Input';
import { Calculator } from '../../Icon/Calculator';
import { SectionContent } from './SectionContent';
import { CurtainHelpModal } from '../CurtainHelpModal';
import { HelpTooltip } from '../../Icon';

const StyledHelpIcon = styled.span`
  width: 42px;
  height: 42px;
  padding-left: 12px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledHeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const MeasurementWrapper = styled.div`
  width: 100%;
  max-width: 300px;
  margin: 10px auto 10px;
`;

const MeasurementLoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: fixed;
  top: 62%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  background: rgb(255, 255, 255, 0.9);
  width: 100%;
  height: 270px;

  @media (min-width: 768px) {
    top: 55%;
  }
`;

const MeasurementLoadingText = styled.span`
  position: absolute;
  top: calc(50% - 100px);
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 100px;
  color: #26375f;
`;

const CalculatePriceMeasurement = styled.div`
  margin: -5px auto 0;
  text-align: right;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
`;

const CalculatePriceMeasurementWrapper = styled.span`
  width: 100%;
  height: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #fbd402;
  border-radius: 40px;
  padding: 14px 17px 14px 41px;
  font-size: var(--bs-sm-font-size);
  text-transform: uppercase;
  font-weight: var(--weight-bold);
  line-height: normal;
  letter-spacing: 1px;

  svg {
    height: 16px;
    margin-right: 7px;
  }
`;

const CalculatePriceMeasurementContent = styled.span`
  margin-left: 4px;
  font-size: 1rem;
  font-size: var(--bs-sm-font-size);
`;

const StyledExtraText = styled.div`
  text-align: center;
  font: 300 12px Lato;
  font-size: var(--bs-sm-font-size);
  color: var(--bs-text);
  margin-top: -8px;

  @media (max-width: 767px) {
    font-size: var(--bs-xs-font-size);
    margin-top: 0px;
  }
`;

const setMeasurements = function (
  width: number | string,
  height: number | string,
  configuration: any,
  send: (event: ProductBuilderMachineEvent) => void,
) {
  if (typeof height !== 'number' || typeof width !== 'number') return;
  let widthValue = width;
  if (
    typeof configuration !== 'undefined' &&
    configuration.includes('Centre Opening')
  ) {
    // Make sure if centre opening is selected it comes to a whole number
    widthValue = Math.round(width / 2) * 2;
  }
  send({
    type: 'UPDATE_CUSTOM',
    custom: {
      measurements: `${widthValue}x${height}`,
      numberOfPanels: undefined,
      panelConfiguration: undefined,
      venetianValance: undefined,
      lightGuards: undefined,
      // Unset add-on products to get price back to $0
      powerSupply: undefined,
      remoteControl: undefined,
      extensionPole: undefined,
      measureInsurancePrice: 0,
      fasciaPrice: 0,
      lightGuardPrice: 0,
      venetianValancePrice: 0,
      trackPrice: 0,
      extensionPolePrice: 0,
      dynamicPrice: 0,
      stainlessSteelChainPrice: 0,
      // Reset curtain-specific addon prices
      wand_colour_price: 0,
      track_colour_price: 0,
      lining_colour_price: 0,
    },
  });

  send({ type: 'GET_PRODUCT_WITH_MEASUREMENTS' });
};

export const Measurements = () => {
  const [state, send] = useService(productBuilderMachine);
  const {
    custom: {
      measurements,
      configuration,
      cellDesign,
      driveControl,
      bladeSize,
      fitting,
      slatSize,
      venetianValance,
    },
  } = state.context;
  const [isHelpModalOpen, setIsHelpModalOpen] = React.useState(false);

  // Only show help modal for curtains
  const isCurtain = state.context.products[0]?.type === 'Curtain';

  const productSubType = getProductType(state.context);
  const baseProductType = getBaseProductType(state.context);
  const windowDepthPerFittingProductTypes = [
    'Honeycomb',
    'Roller Blind',
    'Shutter',
    'Roman Blinds',
  ];
  let isFittingAffectedProductType =
    typeof baseProductType === 'string' &&
    windowDepthPerFittingProductTypes.includes(baseProductType);

  const requiredDepth =
    productSubType && productSubType.includes('Honeycomb')
      ? getRequiredDepthByProductTypeAndCellDesign(
          typeof productSubType === 'string' ? productSubType : '',
          typeof cellDesign === 'string' ? cellDesign : '',
          typeof driveControl === 'string' ? driveControl : '',
        )
      : productSubType && productSubType.includes('Roller')
      ? getRequiredDepthForRollers(
          typeof productSubType === 'string' ? productSubType : '',
          typeof driveControl === 'string' ? driveControl : '',
        )
      : productSubType && productSubType.includes('Roman')
      ? getRequiredDepthForRomans(
          typeof driveControl === 'string' ? driveControl : '',
        )
      : productSubType && productSubType.includes('Shutter')
      ? getRequiredDepthForShutters(
          typeof bladeSize === 'string' ? bladeSize : '',
          typeof fitting === 'string' ? fitting : '',
        )
      : productSubType && productSubType.includes('Aluminium')
      ? getRequiredDepthForVenetians(
          typeof productSubType === 'string' ? productSubType : '',
          typeof venetianValance === 'number' ? true : false,
          typeof slatSize === 'string' ? slatSize : '',
        )
      : [];

  const [width, height] = measurements
    ? (measurements as string).split('x').map(value => parseInt(value))
    : ['', ''];

  // As the user types
  const [tempWidth, setTempWidth] = useState(width);
  const [tempHeight, setTempHeight] = useState(height);

  const baseProduct = getBaseProduct(state.context);
  const currentVariant = getCurrentVariant(state.context);
  const minWidth = getMinWidth(baseProduct);
  let maxWidth = getMaxWidth(baseProduct);
  const smartglideMaxwidth = getSmartglideMaxWidth(baseProduct);
  const rollerDayNightMaxWidth = getRollerDayNightMaxWidth(baseProduct);
  // update max width when user select smartglide forroller
  if (
    productSubType &&
    productSubType.includes('Roller') &&
    driveControl &&
    driveControl.includes('Smartglide')
  ) {
    maxWidth = maxWidth > smartglideMaxwidth ? smartglideMaxwidth : maxWidth;
  }
  // update max width for day/night roller
  if (
    productSubType &&
    productSubType.includes('Roller') &&
    window.location.pathname.includes('roller-day-night')
  ) {
    maxWidth =
      maxWidth > rollerDayNightMaxWidth ? rollerDayNightMaxWidth : maxWidth;
  }

  const minHeight = getMinHeight(baseProduct);
  const maxHeight = getMaxHeight(
    baseProduct,
    getAllowedValue(tempWidth, minWidth, maxWidth),
  );
  const hasInvalidMinMaxValues =
    isNaN(minWidth) || isNaN(maxWidth) || isNaN(minHeight) || isNaN(maxHeight);

  useEffect(() => {
    setTempWidth(width || '');
    setTempHeight(height || '');
  }, [width, height]);
  if (!baseProduct || !currentVariant || hasInvalidMinMaxValues) {
    if (isCurrentState('measurements', state)) {
      throwError(state);
    }
  }

  const measurementsFailed = state.matches('measurements.failure');

  const failedMessage =
    state.matches('measurements.failure') &&
    state.context.productWithMeasurements
      ? state.context.productWithMeasurements
      : 'Failed to find your product! The team has been notified';

  const maxSquareMetre =
    baseProduct && baseProduct.metafields.max_sqm
      ? baseProduct.metafields.max_sqm
      : 0;

  let hideMeasureCaption =
    isFittingAffectedProductType && fitting === 'Face Fit' ? true : false;

  let measurementsPassRequirements =
    tempWidth >= minWidth &&
    tempWidth <= maxWidth &&
    tempHeight >= minHeight &&
    tempHeight <= maxHeight
      ? true
      : false;

  let cordlessHoneycomb =
    productSubType &&
    productSubType.includes('Honeycomb') &&
    productSubType.includes('Cordless')
      ? true
      : false;

  const openHelpModal = () => {
    if (isCurtain) setIsHelpModalOpen(true);
  };

  const closeHelpModal = () => {
    setIsHelpModalOpen(false);
  };

  return (
    <SectionContent>
      {state.matches('measurements.loading') && (
        <MeasurementLoadingWrapper>
          <MeasurementLoadingText>Calculating...</MeasurementLoadingText>
          <Loading
            dots={5}
            size={'1rem'}
            margin={'0.2rem'}
            background={'rgb(50, 115, 136)'}
          />
        </MeasurementLoadingWrapper>
      )}

      <StyledHeadingWrapper>
        <Heading>Size</Heading>
        {isCurtain && (
          <StyledHelpIcon onClick={openHelpModal}>
            <HelpTooltip />
          </StyledHelpIcon>
        )}
      </StyledHeadingWrapper>
      {/* {requiredDepth && requiredDepth.length > 0 && !hideMeasureCaption && (
        <Caption>
          Required depth for your window is {requiredDepth[0].depth}
        </Caption>
      )} */}
      <MeasurementWrapper>
        <MeasurementInput
          name="Width"
          id="Width-input"
          type="number"
          min={minWidth || ''}
          max={maxWidth || ''}
          value={tempWidth}
          showIcon={
            tempWidth >= minWidth && tempWidth <= maxWidth ? true : false
          }
          placeholder="0"
          aria-describedby="width-description"
          disabled={state.matches('measurements.loading')}
          cordlessHoneycomb={cordlessHoneycomb}
          onChange={(
            event: ChangeEvent<{
              value: string;
            }>,
          ) => {
            const value = parseInt(event.target.value);
            setTempWidth(value || '');
          }}
          onBlur={(
            event: ChangeEvent<{
              value: string;
            }>,
          ) => {
            const value = parseInt(event.target.value);
            setTempWidth(value);
            setMeasurements(value, tempHeight, configuration, send);
          }}
        />

        <MeasurementInput
          name="Height"
          id="Height-input"
          type="number"
          min={minHeight || ''}
          max={maxHeight || ''}
          value={tempHeight}
          showIcon={
            tempHeight >= minHeight && tempHeight <= maxHeight ? true : false
          }
          placeholder="0"
          aria-describedby="height-description"
          disabled={state.matches('measurements.loading')}
          cordlessHoneycomb={cordlessHoneycomb}
          onChange={(
            event: ChangeEvent<{
              value: string;
            }>,
          ) => {
            const value = parseInt(event.target.value);
            setTempHeight(value || '');
          }}
          onBlur={(
            event: ChangeEvent<{
              value: string;
            }>,
          ) => {
            const value = parseInt(event.target.value);
            setTempHeight(value);
            setMeasurements(tempWidth, value, configuration, send);
          }}
        />
        {measurementsPassRequirements && (
          <CalculatePriceMeasurement>
            <CalculatePriceMeasurementWrapper>
              <Calculator />
              <CalculatePriceMeasurementContent>
                Get Price
              </CalculatePriceMeasurementContent>
            </CalculatePriceMeasurementWrapper>
          </CalculatePriceMeasurement>
        )}
      </MeasurementWrapper>
      {maxSquareMetre > 0 && (
        <StyledExtraText>
          This product has a maximum square metre size of {maxSquareMetre}m
        </StyledExtraText>
      )}

      {(() => {
        // Show special order message only for Honeycomb Bottom Up (Standard) and Top Down Bottom Up (Dual View) when Corded
        const isHoneycombStandard =
          productSubType &&
          typeof productSubType === 'string' &&
          productSubType.includes('Honeycomb Standard');
        const isHoneycombDualView =
          productSubType &&
          typeof productSubType === 'string' &&
          productSubType.includes('Honeycomb Dual View');
        const isCorded =
          driveControl && driveControl === 'Drive Control: Corded';
        const shouldShowMessage =
          (isHoneycombStandard || isHoneycombDualView) && isCorded;

        return shouldShowMessage ? (
          <StyledExtraText style={{ marginTop: '8px' }}>
            Need a wider blind? We can make 25mm cell corded honeycomb blinds up
            to 3m! Please contact 1800 836 382 to discuss options.
          </StyledExtraText>
        ) : null;
      })()}

      {measurementsFailed === true && <Caption>{failedMessage}</Caption>}
      {isCurtain && (
        <CurtainHelpModal
          modalIsOpen={isHelpModalOpen}
          closeModal={closeHelpModal}
          stepType="measurement"
        />
      )}
    </SectionContent>
  );
};
